import { CalendarClock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import {
  CATCHUP_WINDOW_OPTIONS,
  PRIMARY_OVERLAP_POLICIES,
  SECONDARY_OVERLAP_POLICIES,
} from '~/features/catalog-operations/constants/schedules';
import type {
  CadenceDraft,
  OverlapPolicy,
  SchedulePreview,
} from '~/features/catalog-operations/types/catalog-ops-schedule.types';
import { describeCadenceDraft } from '~/features/catalog-operations/utils/schedule-cadence';
import { cn } from '~/utils/cn';
import { formatInZone } from '~/utils/date';

interface Props {
  draft: CadenceDraft;
  /** `POST /preview` result — the resolved config to display, the duplicate flag
   *  and the upcoming fire times. */
  preview: SchedulePreview | undefined;
  name: string;
  onNameChange: (name: string) => void;
  overlapPolicy: OverlapPolicy;
  onOverlapChange: (policy: OverlapPolicy) => void;
  catchupWindowSeconds: number;
  onCatchupChange: (seconds: number) => void;
  /** Rendered above the form — the create call's own failure. */
  banner?: React.ReactNode;
}

/** Reads the handful of fields the UI surfaces out of the free-form config
 *  snapshot. Everything here is decided by the source job, so it renders as
 *  read-only text: an input would be a control that silently does nothing. */
function readConfig(config: Record<string, unknown> | null) {
  const value = (key: string): unknown => (config ? config[key] : undefined);
  const actions = Array.isArray(value('actions')) ? (value('actions') as string[]) : [];
  const filters = value('filters');
  return {
    // A scheduled fire runs unattended, so the sample-preview stage is dropped.
    // Showing it in the step list would promise a preview nobody will look at.
    actions: actions.filter((a) => a !== 'SAMPLE'),
    hadSample: actions.includes('SAMPLE'),
    filters:
      filters && typeof filters === 'object' ? (filters as Record<string, unknown>) : undefined,
    batchSize: typeof value('batchSize') === 'number' ? (value('batchSize') as number) : null,
    subBatchSize:
      typeof value('subBatchSize') === 'number' ? (value('subBatchSize') as number) : null,
    flow: typeof value('flow') === 'string' ? (value('flow') as string) : null,
  };
}

function OverlapOption({ policy, selected }: { policy: OverlapPolicy; selected: boolean }) {
  const { t } = useTranslation('catalog-operations');
  return (
    <Label
      htmlFor={`overlap-${policy}`}
      data-testid={`schedules-review-overlap-option-${policy}`}
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors',
        selected && 'border-primary bg-accent-100',
      )}
    >
      <RadioGroupItem
        id={`overlap-${policy}`}
        data-testid={`schedules-review-overlap-radio-${policy}`}
        value={policy}
        className="mt-0.5 cursor-pointer"
      />
      <span className="min-w-0 flex-1">
        <span className="text-foreground flex items-center gap-2 text-sm font-medium">
          {t(`schedules.overlap.${policy}.title`)}
          {policy === 'SKIP' && (
            <span className="bg-accent-100 text-primary rounded-full px-2 py-0.5 text-xs font-medium">
              {t('schedules.review.default')}
            </span>
          )}
        </span>
        <span className="text-muted-foreground mt-0.5 block text-xs">
          {t(`schedules.overlap.${policy}.description`)}
        </span>
      </span>
    </Label>
  );
}

/**
 * Step 3: name the schedule, decide what happens when runs collide, and confirm
 * what will actually run. The right column is everything the schedule inherits
 * from its source job and cannot change here.
 */
export function ScheduleReviewStep({
  draft,
  preview,
  name,
  onNameChange,
  overlapPolicy,
  onOverlapChange,
  catchupWindowSeconds,
  onCatchupChange,
  banner,
}: Props) {
  const { t } = useTranslation('catalog-operations');
  const config = readConfig(preview?.requestConfig ?? null);
  const filterEntries = Object.entries(config.filters ?? {});

  return (
    <div data-testid="schedules-review-step" className="space-y-5">
      {banner}

      <div className="grid gap-6 lg:grid-cols-[1.05fr_1fr]">
        <div className="space-y-5">
          <div>
            <Label htmlFor="schedule-name" className="mb-1.5 block text-sm font-medium">
              {t('schedules.review.nameLabel')}
              <span className="text-muted-foreground ml-1 font-normal">
                {t('schedules.review.optional')}
              </span>
            </Label>
            <Input
              id="schedule-name"
              data-testid="schedules-review-name-input"
              value={name}
              onChange={(e) => {
                onNameChange(e.target.value);
              }}
              placeholder={preview?.jobName ?? ''}
            />
            <p className="text-muted-foreground mt-1.5 text-xs">{t('schedules.review.nameHint')}</p>
          </div>

          <div>
            <Label className="mb-2 block text-sm font-medium">
              {t('schedules.review.overlapLabel')}
            </Label>
            <RadioGroup
              data-testid="schedules-review-overlap-radio-group"
              value={overlapPolicy}
              onValueChange={(value) => {
                onOverlapChange(value as OverlapPolicy);
              }}
              className="space-y-2"
            >
              {PRIMARY_OVERLAP_POLICIES.map((policy) => (
                <OverlapOption key={policy} policy={policy} selected={overlapPolicy === policy} />
              ))}
              {/* Accepted by the API but rarely right for catalog work, so they
                  sit behind a disclosure rather than in the main list. */}
              <Collapsible
                defaultOpen={(SECONDARY_OVERLAP_POLICIES as readonly string[]).includes(
                  overlapPolicy,
                )}
              >
                <CollapsibleTrigger
                  data-testid="schedules-review-more-overlap-toggle"
                  className="text-primary cursor-pointer text-sm font-medium"
                >
                  {t('schedules.review.moreOverlap')}
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-2 pt-2">
                  {SECONDARY_OVERLAP_POLICIES.map((policy) => (
                    <OverlapOption
                      key={policy}
                      policy={policy}
                      selected={overlapPolicy === policy}
                    />
                  ))}
                </CollapsibleContent>
              </Collapsible>
            </RadioGroup>
          </div>

          <div>
            <Label className="mb-1.5 block text-sm font-medium">
              {t('schedules.review.catchupLabel')}
            </Label>
            <Select
              value={String(catchupWindowSeconds)}
              onValueChange={(value) => {
                onCatchupChange(Number(value));
              }}
            >
              <SelectTrigger
                data-testid="schedules-review-catchup-select"
                className="cursor-pointer"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent data-testid="schedules-review-catchup-select-content">
                {CATCHUP_WINDOW_OPTIONS.map((seconds) => (
                  <SelectItem
                    key={seconds}
                    value={String(seconds)}
                    data-testid={`schedules-review-catchup-option-${String(seconds)}`}
                    className="cursor-pointer"
                  >
                    {t(`schedules.review.catchup.${String(seconds)}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground mt-1.5 text-xs">
              {t('schedules.review.catchupHint')}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div
            data-testid="schedules-review-summary"
            className="bg-surface-blue rounded-xl border p-4"
          >
            <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              {t('schedules.review.summaryTitle')}
            </p>
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-muted-foreground">{t('schedules.review.batch')}</dt>
              <dd data-testid="schedules-review-summary-batch" className="font-medium">
                {preview?.jobName ?? '—'}
              </dd>
              <dt className="text-muted-foreground">{t('schedules.review.cadence')}</dt>
              <dd data-testid="schedules-review-summary-cadence" className="font-medium">
                {describeCadenceDraft(draft)}
              </dd>
              <dt className="text-muted-foreground">{t('schedules.review.timezone')}</dt>
              <dd data-testid="schedules-review-summary-timezone" className="font-medium">
                {draft.timezone.replace('_', ' ')}
              </dd>
              <dt className="text-muted-foreground">{t('schedules.review.channel')}</dt>
              <dd data-testid="schedules-review-summary-channel" className="font-medium">
                {preview ? `${preview.connector} · ${preview.useCase}` : '—'}
              </dd>
            </dl>

            <p className="text-muted-foreground mt-4 flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase">
              <CalendarClock className="size-3.5" aria-hidden="true" />
              {t('schedules.review.nextRuns')}
            </p>
            {preview && preview.nextRunPreview.length > 0 ? (
              <ul data-testid="schedules-review-next-runs-list" className="mt-2 space-y-1.5">
                {preview.nextRunPreview.slice(0, 3).map((iso, index) => (
                  <li
                    key={iso}
                    data-testid={`schedules-review-next-runs-item-${String(index)}`}
                    className="flex items-baseline gap-3 text-sm"
                  >
                    <span className="text-muted-foreground w-4 font-mono text-xs">{index + 1}</span>
                    <span className="font-medium tabular-nums">
                      {formatInZone(iso, draft.timezone)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p
                data-testid="schedules-review-next-runs-empty"
                className="text-muted-foreground mt-2 text-sm"
              >
                {t('schedules.cadence.noRuns')}
              </p>
            )}
          </div>

          <div
            data-testid="schedules-review-inherited"
            className="bg-surface-blue rounded-xl border p-4"
          >
            <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              {t('schedules.review.inheritedTitle')}
            </p>
            <dl className="mt-3 space-y-2 text-sm">
              {config.flow && (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">{t('schedules.review.pipeline')}</dt>
                  <dd className="text-right font-medium">{config.flow}</dd>
                </div>
              )}
              {config.actions.length > 0 && (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">{t('schedules.review.steps')}</dt>
                  <dd className="text-right font-medium">{config.actions.join(' → ')}</dd>
                </div>
              )}
              {config.hadSample && (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">{t('schedules.review.samplePreview')}</dt>
                  <dd className="text-right font-medium">{t('schedules.review.sampleSkipped')}</dd>
                </div>
              )}
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">{t('schedules.review.filters')}</dt>
                <dd className="text-right font-medium">
                  {filterEntries.length > 0
                    ? filterEntries.map(([key, value]) => `${key}: ${String(value)}`).join(', ')
                    : t('schedules.review.noFilters')}
                </dd>
              </div>
              {config.batchSize !== null && (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">{t('schedules.review.batchSize')}</dt>
                  <dd className="text-right font-medium">
                    {config.batchSize} / {config.subBatchSize ?? '—'}
                  </dd>
                </div>
              )}
              {/* The source job's per-run cap is deliberately not replayed — a
                  schedule imports the whole filtered catalog. */}
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">{t('schedules.review.productCap')}</dt>
                <dd className="text-right font-medium">{t('schedules.review.wholeCatalog')}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">{t('schedules.review.approvals')}</dt>
                <dd className="text-right font-medium">{t('schedules.review.noApprovals')}</dd>
              </div>
            </dl>
            <p className="text-muted-foreground mt-3 text-xs">
              {t('schedules.review.inheritedHint')}
            </p>
            {preview && (
              <p
                data-testid="schedules-review-config-fingerprint"
                className="text-muted-foreground mt-2 font-mono text-[11px] break-all"
              >
                {preview.configFingerprint}
              </p>
            )}
          </div>

          <div className="border-info bg-info-50 text-muted-foreground rounded-lg border border-l-[3px] px-3 py-2.5 text-sm">
            {t('schedules.review.unattendedNote')}
          </div>
        </div>
      </div>
    </div>
  );
}
